import React from "react";
import {
    Box,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    Switch,
    TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { SubOutcome } from "./types/types";
import StageDropdown from "../../../../features/common2/Dropdowns/StageDropdown";
import OutcomeActionDropdown from "../../../../features/common2/Dropdowns/OutcomeActionDropdown";
import ColorDropdown from "../../../../features/common2/Dropdowns/ColorDropdown";
import IconDropdown from "../../../../features/common2/Dropdowns/IconDropdown";
import PlaybookCadenceDropdown from "../../../../features/common2/Dropdowns/PlaybookCadenceDropdown";
import OutcomeStageList from "../../../../features/outcomes/OutcomeStageList";
import RenderIf, { ClaimType, ClaimValue } from "../../../../features/common/RenderIf";

type Props = {
    item: SubOutcome;
    index: number;
    onUpdate: (index: number, prop: string, value: any) => void;
    handleConfirmDelete: (row: SubOutcome) => void;
    options: any[];
};

const SubOutcomeRow = ({ item, index, onUpdate, handleConfirmDelete, options }: Props) => {
    return (
        <Box>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2}>
                    <TextField
                        label="Order"
                        size="small"
                        type="number"
                        value={item.orderBy}
                        onChange={(e) => onUpdate(index, "orderBy", parseInt(e.target.value))}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                    <TextField
                        label="Outcome Name"
                        size="small"
                        fullWidth
                        value={item.name}
                        onChange={(e) => onUpdate(index, "name", e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                        <InputLabel shrink>New Stage</InputLabel>
                        <StageDropdown
                            label="New Stage"
                            value={item.newStageId}
                            onChange={(newValue) => onUpdate(index, "newStageId", newValue)}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                        <InputLabel shrink>Action / Cadence</InputLabel>
                        <OutcomeActionDropdown
                            label="Action / Cadence"
                            value={item.outcomeActionId}
                            onChange={(newValue) => onUpdate(index, "outcomeActionId", newValue)}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                    <FormControl fullWidth>
                        <InputLabel shrink>Color</InputLabel>
                        <ColorDropdown
                            label="Color"
                            sx={{ width: "100%" }}
                            value={item.buttonColor}
                            onChange={(newValue) => onUpdate(index, "buttonColor", newValue)}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                    <FormControl fullWidth>
                        <InputLabel shrink>Icon</InputLabel>
                        <IconDropdown
                            label="Icon"
                            sx={{ width: "100%" }}
                            value={item.icon}
                            onChange={(newValue) => onUpdate(index, "icon", newValue)}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <TextField
                        label="Tooltip"
                        size="small"
                        multiline
                        rows={1}
                        fullWidth
                        value={item.tooltip}
                        onChange={(e) => onUpdate(index, "tooltip", e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    {options.some(
                        (i) =>
                            i.value === item.outcomeActionId &&
                            i.name.toLowerCase().includes("change cadence")
                    ) && (
                            <FormControl fullWidth>
                                <InputLabel shrink>New Cadence</InputLabel>
                                <PlaybookCadenceDropdown
                                    label="New Cadence"
                                    value={item.newPlaybookCadenceId}
                                    onChange={(newValue) => onUpdate(index, "newPlaybookCadenceId", newValue)}
                                />
                            </FormControl>
                        )}
                </Grid>
                <Grid item xs={12} lg={12}>
                    <OutcomeStageList
                        value={item.stages}
                        onChange={(newValue: string[]) => onUpdate(index, "stages", newValue)}
                    />
                </Grid>
                <Grid item xs={12} sm={3} md={1} display="flex" justifyContent="center">
                    <FormControlLabel
                        label="Active"
                        control={
                            <Switch
                                checked={item.isActive}
                                onChange={(e) => onUpdate(index, "isActive", e.target.checked)}
                            />
                        }
                    />
                </Grid>
                <Grid item xs={6} sm={3} md={2} lg={1} display="flex" justifyContent="center">
                    <FormControlLabel
                        label="MI"
                        control={
                            <Switch
                                checked={item.isMeaningfull}
                                onChange={(e) => onUpdate(index, "isMeaningfull", e.target.checked)}
                            />
                        }
                    />
                </Grid>
                <Grid item xs={6} sm={3} md={2} lg={1} display="flex" justifyContent="center">
                    <FormControlLabel
                        label="MQL"
                        control={
                            <Switch
                                checked={item.isMQL}
                                onChange={(e) => onUpdate(index, "isMQL", e.target.checked)}
                            />
                        }
                    />
                </Grid>
                <Grid item xs={6} sm={3} md={2} lg={1} display="flex" justifyContent="center">
                    <FormControlLabel
                        label="DC"
                        control={
                            <Switch
                                checked={item.isDC}
                                onChange={(e) => onUpdate(index, "isDC", e.target.checked)}
                            />
                        }
                    />
                </Grid>
                <Grid item xs={6} sm={3} md={2} lg={1} display="flex" justifyContent="center">
                    <FormControlLabel
                        label="Opportunity"
                        control={
                            <Switch
                                checked={item.isOpportunity}
                                onChange={(e) => onUpdate(index, "isOpportunity", e.target.checked)}
                            />
                        }
                    />
                </Grid>
                <Grid item xs={6} md={1} display="flex" justifyContent="center">
                    <RenderIf claimType={ClaimType.Outcomes} claimValue={ClaimValue.Delete}>
                        <IconButton onClick={() => handleConfirmDelete(item)} title="Delete Sub-Outcome">
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </RenderIf>
                </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
        </Box>
    );
};

export default SubOutcomeRow;
